/**
 * Estado de uma multiseleção dos miolos.
 *
 * Papel: guarda quais opções estão marcadas e escreve o rótulo do botão, com a
 * regra da origem de Manutenções (l. 830–838 e 954–958).
 *
 * Como usar: a tela chama `useMultiSelecao` com todos os valores possíveis
 * daquele filtro e passa `marcados` e `definir` à `MultiSelecao`. O filtro da
 * tabela deixa passar só a linha cujo valor está em `marcados`; com nada
 * marcado, nada passa — a origem não trata "Nenhum" como "sem filtro".
 *
 * Valor que aparece depois do primeiro render — a lista que chega da API, a área
 * nova gravada agora — entra marcado. O que a pessoa desmarcou continua
 * desmarcado enquanto a opção existir. A opção que some é esquecida: se voltar,
 * volta marcada, como na origem, que refaz as caixas a partir da lista atual a
 * cada gravação (l. 951–960). Sem isso, a pessoa que desmarcou "Jardim", excluiu
 * a última corretiva de lá e depois gravou outra não veria a corretiva nova. O
 * hook guarda os valores do render anterior para distinguir a opção nova da
 * desmarcada.
 */
import { useState } from 'react';

interface EstadoDaMultiSelecao {
  conhecidos: ReadonlySet<string>;
  marcados: ReadonlySet<string>;
}

/**
 * O rótulo do botão de uma multiseleção.
 *
 * A precedência é a da origem: tudo marcado é "Todos" mesmo quando não há
 * opção nenhuma; depois, nada marcado é "Nenhum"; o resto é "N de M".
 *
 * @param prefixo - O nome do filtro, como "Área".
 * @param total - Quantas opções o painel mostra.
 * @param marcadas - Quantas delas estão marcadas.
 * @returns O texto do botão, como "Área: 2 de 3".
 */
export function rotuloDaMultiSelecao(
  prefixo: string,
  total: number,
  marcadas: number,
): string {
  if (marcadas === total) return `${prefixo}: Todos`;
  if (marcadas === 0) return `${prefixo}: Nenhum`;
  return `${prefixo}: ${String(marcadas)} de ${String(total)}`;
}

/**
 * Guarda o que está marcado numa multiseleção.
 *
 * @param valores - Todos os valores que o filtro pode ter neste render.
 * @returns `marcados`, com toda opção nova já incluída, e `definir`, que troca
 *   o conjunto inteiro.
 */
export function useMultiSelecao(valores: readonly string[]) {
  const [estado, setEstado] = useState<EstadoDaMultiSelecao>(() => ({
    conhecidos: new Set(valores),
    marcados: new Set(valores),
  }));

  let atual = estado;
  const presentes = new Set(valores);
  const novos = valores.filter((valor) => !estado.conhecidos.has(valor));
  const sumiuAlgum = [...estado.conhecidos].some((valor) => !presentes.has(valor));
  if (novos.length > 0 || sumiuAlgum) {
    // Ajuste de estado durante o render, o padrão do React para estado que
    // deriva de prop: o próximo render já não tem novo nem sumido, e o laço
    // termina.
    atual = {
      conhecidos: presentes,
      marcados: new Set([
        ...[...estado.marcados].filter((valor) => presentes.has(valor)),
        ...novos,
      ]),
    };
    setEstado(atual);
  }

  /**
   * Troca o conjunto de marcados.
   *
   * @param marcados - O conjunto novo, inteiro.
   */
  function definir(marcados: ReadonlySet<string>) {
    setEstado((anterior) => ({
      conhecidos: anterior.conhecidos,
      marcados: new Set(marcados),
    }));
  }

  return { marcados: atual.marcados, definir };
}
